import { useParams, Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { useBlogsFromDB } from "@jambushrushti/hooks/useContent";
import { SocialShareButtons } from "@jambushrushti/components/social/SocialShareButtons";
import {
  ArrowLeft,
  Calendar,
  User,
  BookOpen,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { format, isValid } from "date-fns";

const BlogDetails = () => {
  const { id } = useParams();
  const { data: blogPosts = [], isLoading, error, refetch } = useBlogsFromDB();

  const post = blogPosts.find(p => p.id === id);

  // Get related posts (excluding current)
  const relatedPosts = post
    ? blogPosts.filter(p => p.id !== post.id).slice(0, 3)
    : [];

  if (isLoading) {
    return (
      <Layout>
        <section className="bg-[#E9EEF2] py-20">
          <div className="container mx-auto flex items-center justify-center px-4">
            <Loader2 className="h-8 w-8 animate-spin text-[#4A6FA5]" />
          </div>
        </section>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div role="alert" className="space-y-4 py-20 text-center">
          <p className="text-destructive">{error.message}</p>
          <Button variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <section className="bg-[#E9EEF2] py-20">
          <div className="container mx-auto px-4 text-center">
            <BookOpen className="mx-auto mb-4 h-16 w-16 text-[#DCE3E7]" />
            <h1 className="font-heading mb-4 text-2xl font-bold text-[#2B3A4A]">
              Article Not Found
            </h1>
            <p className="mb-6 text-[#555555]">
              The blog post you're looking for doesn't exist or has been
              removed.
            </p>
            <Button
              className="bg-[#4A6FA5] text-white hover:bg-[#3A5F95]"
              asChild
            >
              <Link to="/community/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Blog
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  // Format date
  const formattedDate =
    post.created_at && isValid(new Date(post.created_at))
      ? format(new Date(post.created_at), "MMMM d, yyyy")
      : "Unknown date";

  const fullContent =
    post.content || post.excerpt || "Content is not available yet.";

  return (
    <Layout>
      {/* Breadcrumb */}
      <PageBreadcrumb
        items={[
          { label: "Blog", href: "/community/blog" },
          { label: post.title },
        ]}
      />

      {/* Hero Image */}
      <section className="relative">
        <div className="aspect-[21/9] max-h-[400px] overflow-hidden">
          <img
            src={
              post.cover_image ||
              "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
            }
            alt={post.title}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#E9EEF2]/90 via-[#E9EEF2]/30 to-transparent" />
        </div>
      </section>

      {/* Article Content */}
      <section className="bg-[#E9EEF2] py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            {/* Header */}
            <div className="animate-fade-up relative z-10 -mt-24 mb-8">
              <Badge className="mb-4 bg-[#4A6FA5] text-white">Blog</Badge>

              <h1 className="font-heading mb-6 text-3xl font-bold text-[#2B3A4A] lg:text-4xl xl:text-5xl">
                {post.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 border-b border-[#DCE3E7] pb-6 text-sm text-[#555555]">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#4A6FA5]/10">
                    <User className="h-5 w-5 text-[#4A6FA5]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2B3A4A]">
                      {post.author || "Jambushrusti Team"}
                    </p>
                    <p className="text-xs">Author</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formattedDate}</span>
                </div>
              </div>
            </div>

            {/* Article Body */}
            <div className="prose prose-lg animate-fade-up max-w-none rounded-xl bg-white p-8 shadow-sm delay-100">
              {fullContent.split("\n\n").map((paragraph, index) => {
                if (paragraph.startsWith("## ")) {
                  return (
                    <h2
                      key={index}
                      className="font-heading mb-4 mt-8 text-2xl font-bold text-[#2B3A4A]"
                    >
                      {paragraph.replace("## ", "")}
                    </h2>
                  );
                }
                if (
                  paragraph.startsWith("1. ") ||
                  paragraph.startsWith("2. ") ||
                  paragraph.startsWith("3. ")
                ) {
                  return (
                    <div key={index} className="my-2">
                      <p
                        className="leading-relaxed text-[#333333]"
                        dangerouslySetInnerHTML={{
                          __html: paragraph.replace(
                            /\*\*(.*?)\*\*/g,
                            "<strong>$1</strong>"
                          ),
                        }}
                      />
                    </div>
                  );
                }
                return (
                  <p
                    key={index}
                    className="mb-4 leading-relaxed text-[#555555]"
                  >
                    {paragraph}
                  </p>
                );
              })}
            </div>

            {/* Share Section */}
            <div className="animate-fade-up mt-12 border-t border-[#DCE3E7] pt-8 delay-200">
              <SocialShareButtons
                title={post.title}
                description={post.excerpt || ""}
              />
            </div>

            {/* Author Box */}
            <div className="animate-fade-up mt-8 rounded-2xl border border-[#DCE3E7] bg-white p-6 delay-300">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#4A6FA5]/10">
                  <User className="h-8 w-8 text-[#4A6FA5]" />
                </div>
                <div>
                  <h3 className="font-heading mb-1 font-bold text-[#2B3A4A]">
                    {post.author || "Jambushrusti Team"}
                  </h3>
                  <p className="mb-3 text-sm text-[#555555]">
                    Contributing Author
                  </p>
                  <p className="text-sm text-[#555555]">
                    A dedicated contributor to Jambushrusti, sharing insights on
                    Jain philosophy, Gurudev's teachings, and the preservation
                    of ancient wisdom.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-white py-16">
          <div className="container mx-auto px-4">
            <div className="mb-10 text-center">
              <span className="text-xs font-medium uppercase tracking-widest text-[#4A6FA5]">
                Continue Reading
              </span>
              <h2 className="font-heading mt-2 text-2xl font-bold text-[#2B3A4A] lg:text-3xl">
                Related Articles
              </h2>
            </div>

            <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
              {relatedPosts.map((relatedPost, index) => (
                <Link
                  key={relatedPost.id}
                  to={`/community/blog/${relatedPost.id}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden border border-[#DCE3E7] transition-all duration-300 hover:border-[#4A6FA5] hover:shadow-lg">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={
                          relatedPost.cover_image ||
                          "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                        }
                        alt={relatedPost.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading line-clamp-2 font-medium text-[#2B3A4A] transition-colors group-hover:text-[#4A6FA5]">
                        {relatedPost.title}
                      </h3>
                      <p className="mt-2 text-xs text-[#555555]">
                        {relatedPost.created_at
                          ? format(
                              new Date(relatedPost.created_at),
                              "MMM d, yyyy"
                            )
                          : "5 min read"}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#2B3A4A] py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4 text-white/70">
            Explore more articles and insights
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              className="bg-[#4A6FA5] text-white hover:bg-[#3A5F95]"
              asChild
            >
              <Link to="/community/blog">
                <BookOpen className="mr-2 h-4 w-4" />
                Browse All Articles
              </Link>
            </Button>
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              asChild
            >
              <Link to="/guruvani">
                Explore Guruvani
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogDetails;
